import { createContext, useContext, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { request } from 'Features/auth/axios-client'

// 'original' = the untouched original schedule, ignoring any mid-week
// reschedules. 'latest' = current state — the most recent mid-week
// reschedule if one exists for this iteration, else the original (resolved
// server-side; see SchedulerService/api/scheduler_view.py's
// `_latest_reschedule_path_segment`). Defaults to 'original' everywhere so
// existing call sites are unaffected.
export type SchedulerDataVariant = 'original' | 'latest'

// Which of the run's alternative answers to show: the engine reports up to
// four (see `scheduler::nsga2::AnswerSet`) and 'balanced' is the run's own
// default answer, written to the normal path. Unrelated to
// `SchedulerDataVariant` above, which is about mid-week reschedule state —
// the two travel as separate query parameters and must not be merged.
//
// An iteration last run by an older engine reports only the first three. The
// drop-down is built from the manifest rows that exist, so a missing
// 'least_disruption' is one fewer option rather than a failed read.
//
// The `step_N` keys are (makespan, disruption) ladder steps the engine found
// that no label covers — the middle answers, e.g. 152h/115h sitting between
// `fastest` at 149h and `fewest_cleans` at 156h. They are rank-indexed, NOT
// stable identities: `step_2` names the second rung of THIS run's ladder, and
// a re-run's `step_2` is a different schedule. That is safe here only because
// the manifest is overwritten per run and the selection is dropped whenever
// the current list no longer offers it. Keys are a fixed small set because
// the value reaches a blob path and SchedulerService allowlists it
// (`nsga2::STAIRCASE_STEP_KEYS`); the engine reports at most
// MAX_EXTRA_STAIRCASE_STEPS = 3 of them.
export type ScheduleVariantKey =
  | 'balanced'
  | 'fastest'
  | 'fewest_cleans'
  | 'least_disruption'
  | 'step_1'
  | 'step_2'
  | 'step_3'
  | 'step_4'
  | 'step_5'

// `null` = the run's OWN answer: the schedule at the iteration's default
// path, whichever key that is (`fastest` since engine v7.176.0, `balanced`
// before — the manifest's `is_default` says which). A key = that answer,
// sent explicitly; SchedulerService decides which blob path the key reads, so
// a key always means the same schedule. Keeping "own answer" as its own value
// rather than guessing its key is what keeps the react-query cache honest: a
// guess made before the manifest arrived would file the default path's rows
// under the wrong key, and `staleTime: Infinity` would keep them there.
export type ScheduleVariantSelection = ScheduleVariantKey | null

export const fetchSchedulerData = async (
  scheduleId: string,
  iteration = '0',
  week?: number,
  variant: SchedulerDataVariant = 'original',
  scheduleVariant: ScheduleVariantSelection = null
) => {
  const params = new URLSearchParams()
  if (week !== undefined) params.set('week', String(week))
  if (variant === 'latest') params.set('variant', 'latest')
  if (scheduleVariant) params.set('scheduleVariant', scheduleVariant)
  const query = params.toString()
  const data = await request({
    url: `/schedulerView/${scheduleId}/${iteration}${query ? `?${query}` : ''}`,
    method: 'GET',
  })
  return data
}

// The answer the planner picked in the schedule drop-down, made ambient.
//
// Every view that shows a run's RESULT — the factory schedule, the full
// Gantt, the utilisation charts, the KPI cards — fetches its own copy through
// `useSchedulerData`, and all of them must follow the selection or the screen
// contradicts itself: a Gantt from one answer beside utilisation from another.
// Threading a prop through each of them would work exactly until someone adds
// the next view and forgets, so the selection travels as context instead and
// every consumer inherits it with no change at the call site.
//
// Default `null` (the run's own answer) means any tree without a provider —
// and any caller that passes the argument explicitly — behaves as before.
export const ScheduleVariantContext =
  createContext<ScheduleVariantSelection>(null)

// The KEY of the run's own answer, for showing it in the drop-down and for
// "is the planner looking at the run's own answer" — the test that decides
// whether the page is editable, comments are open and the stored stat cards
// apply. Provided beside the selection; 'balanced' without a provider, which
// is what every pre-v7.176.0 iteration's default path holds.
export const DefaultScheduleVariantContext =
  createContext<ScheduleVariantKey>('balanced')

/** The run's own answer's key, from the manifest: the row flagged
 *  `is_default`, else 'balanced' (a manifest from an older API, or none). */
export const defaultVariantOf = (
  options?: ScheduleVariantOption[]
): ScheduleVariantKey =>
  options?.find((o) => o.is_default)?.variant_key ?? 'balanced'

/** True when the selection shows the run's own answer — the schedule at the
 *  default path, which is the only one edits, uploads and comments target. */
export const isOwnAnswer = (
  selection: ScheduleVariantSelection | undefined,
  defaultKey: ScheduleVariantKey
) => selection == null || selection === defaultKey

/** `isOwnAnswer` for the answer this tree is showing. */
export const useIsOwnAnswer = () =>
  isOwnAnswer(
    useContext(ScheduleVariantContext),
    useContext(DefaultScheduleVariantContext)
  )

export const useSchedulerData = (
  scheduleId: string,
  iteration: string,
  week?: number,
  variant: SchedulerDataVariant = 'original',
  // Omitted = follow the planner's selection. Pass a value ONLY where a view
  // must ignore it — the mid-week reschedule baseline is the live example,
  // since its checklist is built against the original answer by design.
  // `null` pins the run's own answer.
  scheduleVariant?: ScheduleVariantSelection
) => {
  const queryClient = useQueryClient()
  const selected = useContext(ScheduleVariantContext)
  const effectiveVariant = scheduleVariant ?? selected

  const query = useQuery(
    ['scheduler', scheduleId, iteration, week, variant, effectiveVariant],
    () =>
      fetchSchedulerData(
        scheduleId,
        iteration,
        week,
        variant,
        effectiveVariant
      ),
    {
      select: ({ data }) => data,
      keepPreviousData: true,
      staleTime: Infinity,
    }
  )

  // Prefetch adjacent weeks (same variant) for instant switching
  useEffect(() => {
    if (week !== undefined) {
      const adjacentWeeks = [week - 1, week + 1].filter((w) => w >= 1 && w <= 8)
      adjacentWeeks.forEach((w) => {
        // `effectiveVariant`, NOT the raw prop: callers that follow the
        // planner's selection pass nothing, so the prop is undefined and the
        // prefetch was writing to a key no reader ever asks for — wasted
        // requests, and adjacent-week switching lost its instant feel on
        // every answer but the default.
        queryClient.prefetchQuery(
          ['scheduler', scheduleId, iteration, w, variant, effectiveVariant],
          () =>
            fetchSchedulerData(
              scheduleId,
              iteration,
              w,
              variant,
              effectiveVariant
            ),
          { staleTime: Infinity }
        )
      })
    }
  }, [week, scheduleId, iteration, variant, effectiveVariant, queryClient])

  return query
}

/**
 * Every week's rows of the answer on screen — the iteration's combined output
 * table, read by omitting `week`. Same key as `useSchedulerData` with no week,
 * so the two share a cache entry. `undefined` until loaded, or when there is
 * no schedule to ask about.
 */
export const useAllWeeksSchedulerRows = (
  scheduleId: string | undefined,
  iteration = '0',
  variant: SchedulerDataVariant = 'original'
) => {
  const selected = useContext(ScheduleVariantContext)
  const query = useQuery(
    ['scheduler', scheduleId, iteration, undefined, variant, selected],
    () =>
      fetchSchedulerData(
        scheduleId as string,
        iteration,
        undefined,
        variant,
        selected
      ),
    {
      select: ({ data }) => data,
      staleTime: Infinity,
      enabled: !!scheduleId,
    }
  )
  return query.data?.data as { [key: string]: unknown }[] | undefined
}

// One row of the variant manifest the Rust API writes alongside a run's
// alternative schedules (Versions/{iteration}/Variants/VariantManifestDelta).
export type ScheduleVariantOption = {
  variant_key: ScheduleVariantKey
  label: string
  makespan: number
  cleans: number
  changeovers: number
  // Both optional. They were added to the manifest with the
  // 'least_disruption' answer and the Delta columns are nullable precisely so
  // a manifest written by an older API stays readable — treat `undefined` as
  // "this run did not report it", never as zero, since a schedule with no
  // disruption at all does not exist.
  switches?: number
  disruption_hours?: number
  week_number: number
  // The run's own answer — the one at the iteration's default path. Optional
  // and nullable for the same reason as the two above: a manifest written
  // before engine v7.176.0 has no such column, and then the run's own answer
  // is 'balanced' (`defaultVariantOf`).
  is_default?: boolean | null
}

// Display order for the drop-down. Two rules, applied in this order:
//
//  1. The run's own answer first (`is_default`; 'balanced' on a manifest
//     written before engine v7.176.0, `fastest` since). The first entry is the
//     default selection, so it must not move whatever the manifest returns.
//  2. Everything else by MAKESPAN ascending. The answers are a trade ladder —
//     (makespan, disruption) Pareto steps — so reading down the list is
//     reading the trade: 149h/124h, 152h/115h, 156h/111h, 182h/110h, with
//     disruption falling at every rung. Ordering by key instead would drop a
//     152h middle answer BELOW the 182h least-disruption row and the sequence
//     would stop reading as a ladder at all, which is the whole reason the
//     middle answers are reported.
//
// VARIANT_ORDER survives as the tie-break, so two answers that finish at the
// same time — common, since 'balanced' and 'fastest' are frequently one
// schedule — keep a fixed order instead of shuffling with the manifest read.
// The step keys are listed so an unknown-key indexOf of -1 can never sort a
// ladder step ahead of a label on a tie.
const VARIANT_ORDER: ScheduleVariantKey[] = [
  'balanced',
  'fastest',
  'fewest_cleans',
  'least_disruption',
  'step_1',
  'step_2',
  'step_3',
  'step_4',
  'step_5',
]

const fetchScheduleVariants = async (
  scheduleId: string,
  iteration: string,
  week?: number
) => {
  const query = week !== undefined ? `?week=${week}` : ''
  const data = await request({
    url: `/schedulerView/${scheduleId}/${iteration}/variants${query}`,
    method: 'GET',
  })
  return data
}

// The alternative schedules available for this iteration. An iteration run
// before the feature existed (or by a path that reports none) returns an
// empty list rather than an error, so the caller simply shows no chooser.
export const useScheduleVariants = (
  scheduleId: string,
  iteration: string,
  week?: number
) =>
  useQuery(
    // Deliberately under the same ['scheduler', id, iteration] prefix as
    // useSchedulerData: every "a run finished" path in the Portal invalidates
    // that prefix, and a re-run rewrites the manifest, so a key outside it
    // would leave the drop-down showing the previous run's makespan and clean
    // counts. 'variants' sits in the slot useSchedulerData uses for the week
    // number, so the two can never collide.
    ['scheduler', scheduleId, iteration, 'variants', week],
    () => fetchScheduleVariants(scheduleId, iteration, week),
    {
      // Delta gives no ordering guarantee, and the drop-down's first entry is
      // the default selection — so impose the reported order here rather than
      // letting a read shuffle it. See VARIANT_ORDER above for the two rules.
      // Two unwraps, not one: `request` resolves to the AxiosResponse, and
      // the Service wraps its payload as `{ data: [...], status }`. Spreading
      // one level short spreads that envelope OBJECT, which throws
      // "non-iterable" inside `select` — react-query swallows it into the
      // query's error state, so the drop-down silently fell back to a single
      // "Balanced" entry while the manifest sat there with all three rows.
      select: ({ data }): ScheduleVariantOption[] => {
        const rows = (data?.data ?? []) as ScheduleVariantOption[]
        const own = defaultVariantOf(rows)
        return [...rows].sort((a, b) => {
          if (a.variant_key === b.variant_key) return 0
          if (a.variant_key === own) return -1
          if (b.variant_key === own) return 1
          return (
            a.makespan - b.makespan ||
            VARIANT_ORDER.indexOf(a.variant_key) -
              VARIANT_ORDER.indexOf(b.variant_key)
          )
        })
      },
      // Hold the previous week's list while the next one loads, so the
      // drop-down doesn't empty and re-populate on every week change.
      keepPreviousData: true,
      // Deliberately NOT staleTime: Infinity — a re-run replaces the manifest
      // (the Rust API overwrites it), so a cached list would offer answers the
      // new run never wrote and the read would 404 on a missing variant path.
    }
  )

const getDownloadData = async ({
  scheduleId,
  iteration,
  scheduleVariant = null,
}: {
  scheduleId: string
  iteration: string
  // Which answer to export. The CSV must be the schedule on screen — a
  // download that silently gave the default answer while the page showed
  // "Fastest" would be read as the schedule the planner had just chosen.
  scheduleVariant?: ScheduleVariantSelection
}) => {
  const query = scheduleVariant ? `?scheduleVariant=${scheduleVariant}` : ''
  return request({
    url: `/schedulerView/${scheduleId}/${iteration}/download${query}`,
    method: 'GET',
  })
}

export const useDownloadData = (
  onSuccess: (data: any) => void,
  onError: () => void
) => {
  return useMutation(getDownloadData, {
    onSuccess: (data) => {
      onSuccess(data?.data)
    },
    onError: () => {
      onError()
    },
  })
}

// Create a new POST API to /schedulerView/${scheduleId}/${iteration}

type CreateScheduleVersionProps = {
  scheduleId: string
  iteration: string
  data: Record<string, unknown>
  /** Which week's rows these edits came from. Rows are read per week from
   *  `Week{N}/OutputProductScheduleDelta/`, and that is a DIFFERENT file from
   *  the combined delta, so an edit that omits this writes the combined folder
   *  and the per-week view keeps showing the pre-edit rows. Only the PUT uses
   *  it; POST (create version) ignores it. */
  week?: number
}
const createScheduleVersion = async ({
  scheduleId,
  iteration,
  data,
}: CreateScheduleVersionProps) => {
  return request({
    url: `/schedulerView/${scheduleId}/${iteration}`,
    method: 'POST',
    data,
  })
}

export const useCreateScheduleVersion = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient()
  return useMutation(createScheduleVersion, {
    onSuccess: (_, variables) => {
      // Invalidate every cached `scheduler` query for this schedule so the
      // version-details dropdown picks up the newly created version on any
      // iteration the user navigates to. Without this, the cached
      // `versionDetails` on older iterations (where `staleTime: Infinity`)
      // hides versions created in this session.
      queryClient.invalidateQueries(['scheduler', variables.scheduleId])
      onSuccess()
    },
    onError: () => {
      onError()
    },
  })
}

// Create a new PUT API to /schedulerView/${scheduleId}/${iteration}

const updateScheduleVersion = async ({
  scheduleId,
  iteration,
  data,
  week,
}: CreateScheduleVersionProps) => {
  return request({
    url:
      week === undefined
        ? `/schedulerView/${scheduleId}/${iteration}`
        : `/schedulerView/${scheduleId}/${iteration}?week=${week}`,
    method: 'PUT',
    data,
  })
}

export const useUpdateScheduleVersionTableData = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient()
  return useMutation(updateScheduleVersion, {
    onSuccess: (_, variables) => {
      // Prefix invalidation: the `scheduler` keys carry three more segments
      // after the iteration (week, mid-week variant, reported answer — see
      // `useSchedulerData`), and react-query matches a partial key as a
      // prefix, so this correctly covers every week and answer cached for
      // this iteration rather than only an exact three-segment key.
      queryClient.invalidateQueries([
        'scheduler',
        variables.scheduleId,
        variables.iteration,
      ])
      onSuccess()
    },
    onError: () => {
      onError()
    },
  })
}

// Upload scheduler table view data from CSV file

type UploadSchedulerDataProps = {
  scheduleId: string
  iteration: string
  file: File
}

const uploadSchedulerData = async ({
  scheduleId,
  iteration,
  file,
}: UploadSchedulerDataProps) => {
  const formData = new FormData()
  formData.append('file', file)

  return request({
    url: `/schedulerView/${scheduleId}/${iteration}/upload`,
    method: 'POST',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

export const useUploadSchedulerData = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient()
  return useMutation(uploadSchedulerData, {
    onSuccess: (data, variables) => {
      const resetSchedulerDataPromise = queryClient.invalidateQueries([
        'scheduler',
        variables.scheduleId,
        variables.iteration,
      ])
      resetSchedulerDataPromise.then(() => {
        onSuccess()
      })
    },
    onError: () => {
      onError()
    },
  })
}
